import * as React from 'react';

import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { EnevelopeRotate } from 'src/components/icons';
import { RoleCellBadge } from 'src/components/table/RoleCellBadge';
import { PERMISSION } from 'src/features/auth';
import { commonMessages } from 'src/features/auth/utils';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useDeleteInvitation, useListInvitations } from 'src/features/userManagement/api';
import { useResendInvitation } from 'src/features/userManagement/api/useResendInvitation';
import { InvitationModalForm } from 'src/features/userManagement/components/forms';
import { CellExpiration } from 'src/features/userManagement/components/table/CellExpiration';
import { CellUserManagementActions } from 'src/features/userManagement/components/table/CellUserManagementActions';
import { type Invitation, MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { badgeRoleOptions, userManagementMessages } from 'src/features/userManagement/utils';
import useFilters from 'src/hooks/useFilters';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import {
    CellIndex,
    DataTable,
    DataTablePagination,
    DataTableColumnHeader,
    DataTableColumnGenericHeaderDefinition,
    DataTableControls,
} from 'src/registry/components/table';
import { CellTitleSubtitle } from 'src/registry/components/table/cells/CellTitleSubtitle';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogDescription,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { BadgeOptions } from 'src/types';

const InvitationTable = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const columnHelper = createColumnHelper<Invitation>();
    const permissionInvitationManage = useHasPermissions([PERMISSION.USERS_INVITATION_MANAGE]);

    const [isOpenDeleteInvitationDialogOpen, setIsOpenDeleteInvitationDialog] = React.useState(false);
    const [isOpenResendInvitationDialog, setIsOpenResendInvitationDialog] = React.useState(false);
    const [selectedInvitation, setSelectedInvitation] = React.useState<Invitation | null>(null);

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });

    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: true }],
    });

    const { data, refetch, isLoading, isError } = useListInvitations({
        parkingId,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const { mutate: resendInvitation } = useResendInvitation({
        onSuccess: (_, { email }) => {
            void refetch();
            setSelectedInvitation(null);
            toast({
                title: formatMessage(userManagementMessages.deleteInvitationSuccess),
                description: formatMessage(userManagementMessages.deleteInvitationSuccessDetails, {
                    email,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: deleteInvitation } = useDeleteInvitation({
        onSuccess: (_, { email }) => {
            void refetch();
            setSelectedInvitation(null);
            toast({
                title: formatMessage(userManagementMessages.deleteInvitationSuccess),
                description: formatMessage(userManagementMessages.deleteInvitationSuccessDetails, {
                    email,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const openDeleteInvitationDialog = (invitation: Invitation) => {
        setSelectedInvitation(invitation);
        setIsOpenDeleteInvitationDialog(true);
    };

    const closeDeleteInvitationDialog = (isOpen: boolean) => {
        setSelectedInvitation(null);
        setIsOpenDeleteInvitationDialog(isOpen);
    };

    const openResendInvitationDialog = (invitation: Invitation) => {
        setSelectedInvitation(invitation);
        setIsOpenResendInvitationDialog(true);
    };

    const closeResendInvitationDialog = (isOpen: boolean) => {
        setSelectedInvitation(null);
        setIsOpenResendInvitationDialog(isOpen);
    };

    const allowedRoles: MEMBERSHIP_ROLES[] = useGetParking({ parkingId }).data?.roles || [];
    const filteredBadgeOptions: BadgeOptions = Object.fromEntries(
        Object.entries(badgeRoleOptions).filter(([role]): boolean => allowedRoles.includes(role as MEMBERSHIP_ROLES)),
    );

    const columns = [
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'index',
            cell: CellIndex,
            maxSize: 45,
            meta: { title: '#' },
        }),
        columnHelper.accessor('email', {
            id: 'email',
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userManagementMessages.email),
            },
        }),
        columnHelper.accessor('inviter.email', {
            id: 'inviter__email',
            header: DataTableColumnHeader,
            cell: (cellProps) =>
                CellTitleSubtitle({
                    title: cellProps.row.original.inviter.name,
                    subtitle: cellProps.row.original.inviter.email,
                    ...cellProps,
                }),
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(userManagementMessages.invitedBy),
            },
        }),
        columnHelper.accessor('role', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: RoleCellBadge,
            meta: {
                title: formatMessage(userManagementMessages.role),
                badgeOptions: filteredBadgeOptions,
            },
        }),
        // As expiration_date is a computed field, we need to use created_at for sorting
        columnHelper.accessor('expiration_date', {
            id: 'is_expired',
            header: DataTableColumnHeader,
            cell: CellExpiration,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(userManagementMessages.expires),
                filterLabel: formatMessage(userManagementMessages.status),
                customDropdownMenuLabel: formatMessage(userManagementMessages.filterByLabel, {
                    value: `"${formatMessage(userManagementMessages.status)}"`,
                }),
                options: [
                    { value: 'false', label: formatMessage(userManagementMessages.invitationActive) },
                    { value: 'true', label: formatMessage(userManagementMessages.invitationExpired) },
                ],
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            maxSize: 60,
            cell: (props) => (
                <CellUserManagementActions
                    resendCallback={openResendInvitationDialog}
                    deleteCallback={openDeleteInvitationDialog}
                    {...props}
                />
            ),
        }),
    ];

    return (
        <>
            <Dialog open={isOpenResendInvitationDialog} onOpenChange={closeResendInvitationDialog}>
                <DialogContent>
                    <DialogHeader className="flex-row gap-4">
                        <div className="flex flex-row gap-2">
                            <SquareIcon>
                                <EnevelopeRotate className="size-5" />
                            </SquareIcon>
                            <div>
                                <DialogTitle>{formatMessage(userManagementMessages.resendInvitation)}</DialogTitle>
                                <DialogDescription>
                                    {formatMessage(userManagementMessages.resendInvitationTo, {
                                        email: selectedInvitation?.email,
                                    })}
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                    <DialogSeparator />

                    <DialogBody>
                        <p className="text-gray-500">
                            {formatMessage(userManagementMessages.confirmResendInvitation, {
                                email: selectedInvitation?.email,
                            })}
                        </p>
                    </DialogBody>

                    <DialogSeparator />

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                {formatMessage(generalMessages.cancel)}
                            </Button>
                        </DialogClose>

                        <Button
                            onClick={() => {
                                if (selectedInvitation) {
                                    void resendInvitation({ ...selectedInvitation, parkingId });
                                }
                            }}
                            className="flex-1"
                        >
                            {formatMessage(generalMessages.send)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isOpenDeleteInvitationDialogOpen} onOpenChange={closeDeleteInvitationDialog}>
                <DialogContent>
                    <DialogHeader className="flex-row gap-4">
                        <div className="flex flex-row gap-2">
                            <SquareIcon icon={faTrashAlt} />
                            <div>
                                <DialogTitle>{formatMessage(userManagementMessages.deleteInvitation)}</DialogTitle>
                                <DialogDescription>
                                    {formatMessage(userManagementMessages.deleteInvitationFor, {
                                        email: selectedInvitation?.email,
                                    })}
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <DialogSeparator />

                    <DialogBody>
                        <p className="text-gray-500">
                            {formatMessage(userManagementMessages.confirmDeleteInvitation, {
                                email: selectedInvitation?.email,
                            })}
                        </p>
                    </DialogBody>

                    <DialogSeparator />

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                {formatMessage(generalMessages.cancel)}
                            </Button>
                        </DialogClose>

                        <Button
                            variant="destructiveLight"
                            onClick={() => {
                                if (selectedInvitation) {
                                    void deleteInvitation({ ...selectedInvitation, parkingId });
                                    setIsOpenDeleteInvitationDialog(!isOpenDeleteInvitationDialogOpen);
                                }
                            }}
                            className="flex-1"
                        >
                            {formatMessage(userManagementMessages.deleteInvitation)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DataTable
                columns={columns}
                data={data}
                pagination={pagination}
                sorting={sorting}
                isLoading={isLoading}
                isError={isError}
                refetch={refetch}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(userManagementMessages.invitations),
                    entryName: formatMessage(userManagementMessages.invitation),
                    entryNames: formatMessage(userManagementMessages.invitations),
                    placeholder: formatMessage(commonMessages.email),
                }}
                components={{
                    controls: DataTableControls,
                    newEntry: permissionInvitationManage ? InvitationModalForm : undefined,
                    pagination: DataTablePagination,
                }}
            />
        </>
    );
};

export { InvitationTable };
